import { legalServiceSchema } from "@/lib/firm";
import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import { CONVEYANCING_LOCATIONS } from "@/lib/conveyancing-locations";
import { SELL_LOCATIONS, getSellLocation, type SellLocation } from "@/lib/sell-locations";

const SITE = "https://coastsidelaw.com.au";

export const Route = createFileRoute("/selling/$suburb")({
  loader: ({ params }) => {
    const content = getSellLocation(params.suburb);
    if (!content) throw notFound();
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/selling/${params.suburb}`;
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.description) },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(content.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `${FIRM.name} — sale conveyancing, ${content.name}`,
            description: content.description,
            telephone: FIRM.phone,
            address: legalServiceSchema.address,
            parentOrganization: { "@id": "https://coastsidelaw.com.au/#legalservice" },
            areaServed: [content.name, ...content.nearby].map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            serviceType: [
              "Sale conveyancing",
              "Seller disclosure statement",
              "Contract review",
              "Electronic settlement",
            ],
            offers: [
              {
                "@type": "Offer",
                name: "Fixed-fee sale conveyancing",
                price: FEES.sale.replace("$", "").replace(",", ""),
                priceCurrency: "AUD",
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Selling", item: `${SITE}/selling` },
              { "@type": "ListItem", position: 3, name: content.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: SellLocationPage,
});

function SellLocationPage() {
  const { content } = Route.useLoaderData() as { content: SellLocation };
  const others = SELL_LOCATIONS.filter((l) => l.slug !== content.slug);
  const hasConveyancingPage = CONVEYANCING_LOCATIONS.some((l) => l.slug === content.slug);

  return (
    <>
      <PageHero eyebrow={`Selling — ${content.name}`} title={content.title} lead={content.lead} />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Sale conveyancing
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.sale}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.saleNote}</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Contract review before signing
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.contractReview}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.contractReviewNote}</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Searches, certificates and registry fees are charged at cost. No professional fee if the
          contract is terminated during the cooling-off, finance or building and pest period. See
          the{" "}
          <Link to="/selling" className="font-semibold text-primary underline">
            full selling page
          </Link>{" "}
          for what the fee covers.
        </p>

        {content.paragraphs.map((p) => (
          <p key={p} className="mt-6 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What comes up on a {content.name} sale
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.points.map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we meet you</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Our Sunshine Coast office is at {FIRM_ADDRESS} and our peninsula office at{" "}
          {PENINSULA_ADDRESS}, both by appointment. We act for sellers across {content.name} and{" "}
          {content.nearby.join(", ")}, and a sale file runs perfectly well by email and electronic
          settlement.
        </p>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={content.faqs} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Related pages</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/seller-disclosure" className="font-semibold text-primary underline">
              Seller disclosure statements in Queensland
            </Link>
          </li>
          {hasConveyancingPage && (
            <li>
              <Link
                to="/conveyancing/$suburb"
                params={{ suburb: content.slug }}
                className="font-semibold text-primary underline"
              >
                Conveyancing in {content.name}
              </Link>
            </li>
          )}
          <li>
            <Link to="/conveyancing" className="font-semibold text-primary underline">
              Fixed-fee conveyancing — sale and purchase
            </Link>
          </li>
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Selling in nearby areas</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {others.map((l) => (
            <li key={l.slug}>
              <Link
                to="/selling/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Selling in {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-14">
          <EnquiryForm heading={`Get a fixed-fee quote for your ${content.name} sale`} />
        </div>
      </section>

      <CtaBand
        title="Talk to us before you sign the agent's paperwork"
        body={`Call ${FIRM.phone} or send the address through. We will tell you what the disclosure statement needs and confirm the fee in writing.`}
      />
      <div className="h-20" />
    </>
  );
}
